import { NgIf } from '@angular/common';
import { Component, effect, inject, OnDestroy } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { Router, RouterLink } from '@angular/router';
import { FuseLoadingService } from '@fuse/services/loading';
import { TranslocoPipe } from '@jsverse/transloco';
import { CompanyTreeService } from 'app/core/company/company-tree.service';
import { WorkingCompany } from 'app/core/company/working-company';
import { useCompanyAccessStatus } from 'app/core/helpers/company-access.helper';
import { BasePaginatedResponse } from 'app/core/http/base-paginated-response';
import { Key, KeyType } from 'app/core/key/key.types';
import { KeysService } from 'app/core/key/keys.service';
import { DistributorCompanyCardComponent } from 'app/layout/common/distributor-company-card/distribuitor-company-card.component';
import { LinkCompanyCardComponent } from "app/layout/common/link-company-card/link-company-card.component";
import { TitleWithIconComponent } from "app/layout/common/title-with-icon/title-with-icon.component";
import { Kpi } from 'app/modules/admin/common/kpi-widget/kpi';
import { KpiWidgetComponent } from 'app/modules/admin/common/kpi-widget/kpi-widget.component';
import { DeviceKpisService } from 'app/modules/admin/devices/device-kpis/device-kpis.service';
import {
    CompanyDevicesFailuresWidgetComponent
} from 'app/modules/admin/overview/company-devices-failures-widget/company-devices-failures-widget.component';
import { ScheduledActionService } from 'app/modules/admin/scheduled-actions/scheduled-action.service';
import { DateTime } from 'luxon';
import { NgApexchartsModule } from 'ng-apexcharts';
import { forkJoin, map } from 'rxjs';

/* Get the current instant */
const now = DateTime.now();

@Component({
  selector: 'app-overview',
  standalone: true,
    imports: [
    MatIcon,
    TranslocoPipe,
    KpiWidgetComponent,
    NgIf,
    MatProgressBar,
    RouterLink,
    NgApexchartsModule,
    CompanyDevicesFailuresWidgetComponent,
    TitleWithIconComponent,
    LinkCompanyCardComponent,
    MatProgressSpinner,
    DistributorCompanyCardComponent
],
    styleUrls: ['./overview.component.scss'],
  templateUrl: './overview.component.html'
})
export class OverviewComponent implements OnDestroy {
    private readonly _router = inject(Router);
    private readonly _deviceKpisService = inject(DeviceKpisService);
    private readonly _keysService = inject(KeysService);
    private readonly _scheduledActionService = inject(ScheduledActionService);
    protected readonly _workingCompany = inject(WorkingCompany);
    private readonly _fuseLoadingService = inject(FuseLoadingService);
    private readonly _companyTreeService = inject(CompanyTreeService);


    readonly companyStatus = useCompanyAccessStatus();
    readonly treeCompanyStatus = useCompanyAccessStatus(this._companyTreeService.selectedCompany);

    loadingFailures = false;

    deviceKpis: Kpi[] = [];

    totalDevices: number;
    uniqueDevices: Set<number>;

    totalActiveKeys: number;
    subtotalSelectorKeys: number;
    subtotalVirtualKeys: number;

    totalScheduledActions: number;
    subtotalScheduledDevices: number;
    subtotalNonScheduledDevices: number;
    subtotalScheduledDevicesPercentage: number;
    subtotalNonScheduledDevicesPercentage: number;
    showDistributorClientCard: boolean = false;

    // TODO company change or timer elapsed.
    onCompanyChangeUpdateOverview = effect(() => {
        const companyId = this._workingCompany.id;

        if(!this.companyStatus().isRestricted && !this.treeCompanyStatus().shouldShowDistributorClientCard) {
            this.getCompanyOverviewData(companyId);
            this.getCompanyKeysKpis(companyId);
        }
    });

    showDistributorClientCardEffect = effect(() => {
        this.showDistributorClientCard = this.treeCompanyStatus().shouldShowDistributorClientCard;
    });


    constructor() {
        this._fuseLoadingService.setAutoMode(false);
    }

    getCompanyOverviewData(companyId: number) {
        forkJoin({
            actions: this._scheduledActionService.getAllCompanyScheduledActions(companyId),
            deviceKpis: this._deviceKpisService.getAllDeviceKpis(companyId).pipe(
                map(kpis => kpis.filter(kpi => kpi.title !== 'Open issues'))
            )
        }).subscribe(({ actions, deviceKpis }) => {
            // Provisorio para ordenar KPIS hasta modificar el backend
            const sortedKpis = deviceKpis.slice().sort((a, b) => {
                if (a.title === 'Devices with errors') return 1;
                if (b.title === 'Devices with errors') return -1;
                return 0;
            });

            //Este cambio puede que sea mejor hacerlo en el Back-end, se deja aqui de manera provisoria con el de ordenamiento.
            const connectedDevicesKpi = sortedKpis.find(kpi => kpi.title === 'Connected devices');
            const connectedDevicesValue = connectedDevicesKpi ? connectedDevicesKpi.value : 0;

            if (connectedDevicesKpi) {
            this.deviceKpis = sortedKpis.map(kpi => {
                const adjustedTotal = (kpi.title === 'Devices with warnings' || kpi.title === 'Devices with errors')
                    ? connectedDevicesValue
                    : kpi.total;

                let adjustedValue = kpi.value;
                if (kpi.title === 'Devices with warnings' || kpi.title === 'Devices with errors') {
                    adjustedValue = Math.min(kpi.value, connectedDevicesValue);
                }   

                return new Kpi(
                    kpi.title,
                    adjustedValue,
                    adjustedTotal
                );
            });
        } else {
            this.deviceKpis = [];
        }

            this.totalDevices = deviceKpis[0]?.total ?? 0;

            this.totalScheduledActions = actions.length;
            this.uniqueDevices = new Set<number>();
            actions.forEach(action => {
                action.machinesIds.forEach(machineId => this.uniqueDevices.add(machineId));
            });

            this.subtotalScheduledDevices = this.uniqueDevices.size;
            this.subtotalNonScheduledDevices = this.totalDevices - this.uniqueDevices.size;

            this.subtotalScheduledDevicesPercentage = this.totalDevices > 0
                ? (this.subtotalScheduledDevices / this.totalDevices) * 100
                : 0;

            this.subtotalNonScheduledDevicesPercentage = this.totalDevices > 0
                ? (this.subtotalNonScheduledDevices / this.totalDevices) * 100
                : 0;
        });
    }

    onKpiClick(title: string): void {
        const titleToFilterMap = {
            'Connected devices': {connected_true:true,},
            'Devices with warnings': {connected_true:true, failureTypes_warning: true},
            'Devices with errors': {connected_true:true, failureTypes_error:true}
        } as const;

        const filters = titleToFilterMap[title as keyof typeof titleToFilterMap];

        if (!filters) {
            this._router.navigate(['/devices']);
            return;
        }

        const filterNamespace = `deviceFilters_${this._workingCompany.id}`;

        const newFilters = {
            ...filters,
        };

        localStorage.setItem(filterNamespace, JSON.stringify(newFilters));
        this._router.navigate(['/devices']);
    }

    onLoadingFailuresChange(value: boolean): void {
        queueMicrotask(() => {
            this.loadingFailures = value;
        });
    }

    getCompanyKeysKpis(companyId: number) {
        this._keysService.getAllCompanyKeys(companyId).pipe(
            map((response: BasePaginatedResponse<Key>) => response.queryResult)
        ).subscribe(keysResponse => {
            const keys = keysResponse ?? []; 
            this.totalActiveKeys = keys.filter(k => k.active).length;
            this.subtotalSelectorKeys = keys.filter(k => k.active && k.typeKeyId === KeyType.Selector)?.length;
            this.subtotalVirtualKeys = keys.filter(k => k.active && k.typeKeyId === KeyType.Virtual)?.length;
        });
    }

    ngOnDestroy(): void {
        this._fuseLoadingService.setAutoMode(true);
    }
}